<div class="tab-content api-key-management">
  <p class="double-margin-bottom">
    Manage API keys for programmatic access to your Arpio account. API keys can be assigned roles to control their
    permissions.
  </p>
  <div class="flex justify-between margin-top">
    <div class="flex items-center margin-bottom">
      <button
        *ngIf="canManageApiKeys$ | async"
        class="arpio-button"
        (click)="openNewApiKeyModal()"
        data-cy="create-api-key-button"
      >
        <i class="fa-utility fa-semibold fa-plus text-sm"></i>Create New API Key
      </button>
    </div>
    <div class="half-margin-bottom">
      <div class="filter-container">
        <input pInputText #filterInput type="text" (input)="filterGlobal($event)" placeholder="Search..." />
        <div
          *ngIf="!isFilterApplied && !loadingFilterInput"
          class="fa-utility-fill fa-semibold fa-filter fade-icon"
        ></div>
        <div
          *ngIf="isFilterApplied && !loadingFilterInput"
          (click)="clearInput()"
          class="fa-utility fa-semibold fa-xmark"
        ></div>
        <div *ngIf="loadingFilterInput" class="fa-solid fa-spinner fa-spin"></div>
      </div>
    </div>
  </div>

  <!-- Unauthorized State -->
  <div *ngIf="(canViewApiKeys$ | async) === false" class="unauthorized-container" data-cy="unauthorized-message">
    <div class="unauthorized-content">
      <h3>Access Restricted</h3>
      <p>{{ unauthorizedMessage$ | async }}</p>
    </div>
  </div>

  <!-- Authorized Content -->
  <div *ngIf="(canViewApiKeys$ | async) === true && apiKeys != null; else loadingBlock" class="table-container">
    <!-- Empty State -->
    <div *ngIf="apiKeys.length === 0" class="empty-state-container">
      <div class="empty-state-content">
        <h3>No API keys have been created yet</h3>
        <p>API keys allow programmatic access to your Arpio account. Create your first API key to get started.</p>
        <button *ngIf="canManageApiKeys$ | async" class="p-button p-button-primary" (click)="openNewApiKeyModal()">
          <i class="fa-utility fa-semibold fa-plus"></i>
          Create Your First API Key
        </button>
      </div>
    </div>

    <!-- Table Section -->
    <div *ngIf="apiKeys.length > 0" class="table-section main-table">
      <p-table
        #dt1
        [value]="apiKeys"
        [globalFilterFields]="globalFilterFields"
        data-cy="api-key-table"
        dataKey="id"
        [scrollable]="true"
        scrollHeight="60vh"
        [tableStyle]="{ 'min-width': '30rem' }"
        [styleClass]="'p-datatable-sm arp-prime-datatable'"
        [rowHover]="true"
        [totalRecords]="apiKeys?.length"
        [paginator]="false"
        [rowTrackBy]="apiKeyTrackBy"
        selectionMode="single"
        [(selection)]="selectedTableItem"
        (onRowSelect)="onRowSelect($event.data)"
        [customSort]="true"
        (sortFunction)="customSort($event)"
      >
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="name" class="strong-text name-column">Name <p-sortIcon field="name"></p-sortIcon></th>
            <th pSortableColumn="enabled" class="strong-text enabled-column">
              Status <p-sortIcon field="enabled"></p-sortIcon>
            </th>
            <th pSortableColumn="roleCount" class="strong-text role-count-column">
              Roles <p-sortIcon field="roleCount"></p-sortIcon>
            </th>
            <th pSortableColumn="createdAt" class="strong-text created-at-column">
              Created <p-sortIcon field="createdAt"></p-sortIcon>
            </th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-apiKey let-index="rowIndex">
          <tr
            [pSelectableRow]="apiKey"
            class="cursor-pointer"
            [ngClass]="{
              'selected-row': showSidePanel && selectedApiKey && selectedApiKey.id === apiKey.id,
              'refreshing-row': isApiKeyRefreshing(apiKey),
            }"
          >
            <td class="name-column" style="position: relative">
              <div class="apikey-info">
                <div class="rbac-table-display-container">
                  <i class="fa-solid fa-spinner fa-spin half-margin-right" *ngIf="isApiKeyRefreshing(apiKey)"></i>
                  <span>{{ apiKey.name }}</span>
                </div>
              </div>
            </td>
            <td class="enabled-column">
              <span>{{ apiKey.enabled ? 'Enabled' : 'Disabled' }}</span>
            </td>
            <td class="role-count-column">
              <div class="flex items-center">
                <i class="fa-solid fa-spinner fa-spin half-margin-right" *ngIf="isApiKeyRefreshing(apiKey)"></i>
                <div *ngIf="apiKey.roles.length !== 0; else noRoles" class="roles-list">
                  <p-tag
                    *ngFor="let role of apiKey.roles | slice: 0 : 2"
                    severity="info"
                    class="role-tag clickable-tag fit-width"
                    (click)="navigateToRole(role.name)"
                    (keydown.enter)="navigateToRole(role.name)"
                    (keydown.space)="navigateToRole(role.name)"
                    tabindex="0"
                    value="{{ role.name }}"
                  >
                  </p-tag>
                  <span
                    *ngIf="apiKey.roles.length > 2"
                    class="more-count"
                    [pTooltip]="getAdditionalRolesTooltip(apiKey)"
                    tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                    tooltipPosition="top"
                    [autoHide]="false"
                    [hideDelay]="200"
                    [escape]="false"
                    tooltipEvent="hover"
                  >
                    +{{ apiKey.roles.length - 2 }} more
                  </span>
                </div>
                <ng-template #noRoles><em>None</em></ng-template>
              </div>
            </td>
            <td class="created-at-column">
              <div class="rbac-table-display-container">
                <span
                  [pTooltip]="getLastModifiedTooltip(apiKey.createdAt)"
                  tooltipStyleClass="arp-tooltip"
                  tooltipPosition="top"
                  [autoHide]="false"
                  [hideDelay]="200"
                  tooltipEvent="hover"
                  >{{ getLastModifiedDisplay(apiKey.createdAt) }}</span
                >
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="4" class="empty-state-cell text-center">
              <div class="empty-state-content">
                <p class="empty-state-message">No results found</p>
                <p class="empty-state-submessage">Try adjusting your search or filter criteria.</p>
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="summary">
          <div class="flex align-items-center justify-content-between">
            <span
              >Displaying {{ dt1.filteredValue?.length ?? dt1.value?.length }} of {{ dt1.value?.length }} API keys</span
            >
          </div>
        </ng-template>
      </p-table>
    </div>

    <!-- Side Panel -->
    <div class="side-panel" [ngClass]="{ open: showSidePanel }" #sidePanel>
      <div class="side-panel-header">
        <div class="flex items-center">
          <button class="close-btn" (click)="closeSidePanel()">
            <i class="fa-utility fa-semibold fa-angle-right"></i>
          </button>
          <h3>API Key Details</h3>
        </div>
        <div>
          <button *ngIf="canManageApiKeys$ | async" class="edit-button" (click)="openEditApiKeyModal()">
            <i class="fa-utility fa-semibold fa-pen-to-square"></i>
          </button>
        </div>
      </div>

      <div class="side-panel-content">
        <!-- Basic Information Section -->
        <div class="detail-section">
          <div class="detail-item">
            <strong>Name:</strong>
            <span *ngIf="showSidePanel && selectedApiKey">{{ selectedApiKey.name }}</span>
            <div *ngIf="!showSidePanel || !selectedApiKey" class="detail-skeleton"></div>
          </div>
          <div class="detail-item">
            <strong>Status:</strong>
            <span *ngIf="showSidePanel && selectedApiKey">
              <p-tag
                [severity]="selectedApiKey.enabled ? 'success' : 'danger'"
                [value]="selectedApiKey.enabled ? 'Enabled' : 'Disabled'"
              ></p-tag>
            </span>
            <div *ngIf="!showSidePanel || !selectedApiKey" class="detail-skeleton"></div>
          </div>
          <div class="detail-item">
            <strong>API Key ID:</strong>
            <span *ngIf="showSidePanel && selectedApiKey" class="monospace">{{ selectedApiKey.id }}</span>
            <div *ngIf="!showSidePanel || !selectedApiKey" class="detail-skeleton"></div>
          </div>
        </div>

        <!-- Roles Section -->
        <div class="detail-section">
          <h4 *ngIf="showSidePanel && selectedApiKey">Roles ({{ selectedApiKey.roles.length }})</h4>
          <h4 *ngIf="!showSidePanel || !selectedApiKey">Roles</h4>

          <div class="roles-list detail-item">
            <ng-container *ngIf="showSidePanel && selectedApiKey">
              <p-tag
                *ngFor="let role of selectedApiKey.roles"
                [value]="role.name"
                severity="info"
                class="role-tag clickable-tag"
                (click)="navigateToRole(role.name)"
                (keydown.enter)="navigateToRole(role.name)"
                (keydown.space)="navigateToRole(role.name)"
                tabindex="0"
              ></p-tag>
              <span *ngIf="selectedApiKey.roles.length === 0" class="text-muted"><em>No roles assigned</em></span>
            </ng-container>
            <ng-container *ngIf="!showSidePanel || !selectedApiKey">
              <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
              <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
              <div class="tag-skeleton"></div>
            </ng-container>
          </div>
        </div>

        <!-- Additional Information Section -->
        <div class="detail-section">
          <h4>Additional Information</h4>

          <div class="detail-item">
            <strong>Created:</strong>
            <span *ngIf="showSidePanel && selectedApiKey">{{ selectedApiKey.createdAt | date: 'medium' }}</span>
            <div *ngIf="!showSidePanel || !selectedApiKey" class="detail-skeleton"></div>
          </div>

          <div class="detail-item" *ngIf="selectedApiKey?.lastUpdatedAt">
            <strong>Last Modified:</strong>
            <span *ngIf="showSidePanel && selectedApiKey">{{ selectedApiKey.lastUpdatedAt | date: 'medium' }}</span>
            <div *ngIf="!showSidePanel || !selectedApiKey" class="detail-skeleton"></div>
          </div>
        </div>

        <!-- Action Buttons Section -->
        <div
          class="detail-section action-buttons"
          *ngIf="showSidePanel && selectedApiKey && (canManageApiKeys$ | async)"
        >
          <h4>Actions</h4>
          <div class="margin-auto text-center margin-top">
            <a class="delete-role-button text-center" (click)="deleteApiKey()">Delete API Key...</a>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template #loadingBlock> <i class="fa-solid fa-spinner fa-spin"></i> Loading API keys... </ng-template>

  <app-new-api-key-modal #newApiKeyModal (apiKeyCreated)="onApiKeyCreated()"></app-new-api-key-modal>
  <app-edit-api-key-modal #editApiKeyModal (apiKeyUpdated)="onApiKeyUpdated($event)"></app-edit-api-key-modal>

  <!-- Delete API Key Confirmation Dialog -->
  <modal-dialog #deleteApiKeyDialog [dialogTemplate]="confirmDeleteApiKeyDialog"></modal-dialog>
  <ng-template #confirmDeleteApiKeyDialog>
    <confirm-action-dialog
      name="&quot;{{ apiKeyToDelete?.name }}&quot;"
      action="Delete"
      [error]="deleteApiKeyDialog.error"
      (onCancel)="cancelDeleteApiKey()"
      (onAction)="confirmDeleteApiKey()"
    >
      <div class="error margin-top">
        Deleting this API key will revoke its access immediately. This action cannot be undone.
      </div>
    </confirm-action-dialog>
  </ng-template>
</div>
